import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, AreaChart, Area
} from 'recharts';

const STATUS_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];
const SEVERITY_COLOR_MAP = { critical: '#dc2626', high: '#f97316', medium: '#f59e0b' };

function InvestigationStatusChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200" data-testid="chart-inv-status">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Investigation Status</h3>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={38} paddingAngle={3}>
            {data.map((entry, i) => (
              <Cell key={entry.name} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
        {data.map((entry, i) => (
          <div key={entry.name} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }} />
            <span className="text-xs text-slate-600 capitalize">{entry.name} ({entry.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeverityChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200" data-testid="chart-severity">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Incidents by Severity</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} barSize={32}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="severity" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {data.map(entry => (
              <Cell key={entry.severity} fill={SEVERITY_COLOR_MAP[entry.severity] || '#22c55e'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SectorDistributionChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200" data-testid="chart-sector">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Sector Distribution</h3>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="sector" cx="50%" cy="50%" outerRadius={65} innerRadius={38} paddingAngle={3}>
            <Cell fill="#3b82f6" />
            <Cell fill="#06b6d4" />
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-6 mt-2">
        {data.map((entry, i) => (
          <div key={entry.sector} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: i === 0 ? '#3b82f6' : '#06b6d4' }} />
            <span className="text-xs text-slate-600 capitalize">{entry.sector} ({entry.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IncidentTimelineChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 md:col-span-2" data-testid="chart-timeline">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Incident Timeline</h3>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Area type="monotone" dataKey="aviation" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
          <Area type="monotone" dataKey="maritime" stackId="1" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 mt-2 justify-center">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-xs text-slate-600">Aviation</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-cyan-500" /><span className="text-xs text-slate-600">Maritime</span></div>
      </div>
    </div>
  );
}

function TaskProgressChart({ data }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200" data-testid="chart-tasks">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Outfit' }}>Task Progress by Module</h3>
      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
        {data.slice(0, 14).map(spec => {
          const pct = spec.total > 0 ? Math.round((spec.completed / spec.total) * 100) : 0;
          return (
            <div key={spec.specialist} className="flex items-center gap-2">
              <span className="text-xs text-slate-600 w-24 truncate shrink-0" title={spec.specialist}>{spec.specialist}</span>
              <div className="flex-1 bg-slate-100 rounded-full h-2">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-slate-500 w-14 text-right shrink-0">{spec.completed}/{spec.total}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AnalyticsCharts({ analytics }) {
  if (!analytics) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-testid="analytics-section">
      <InvestigationStatusChart data={analytics.investigation_statuses} />
      <SeverityChart data={analytics.severity_breakdown} />
      <SectorDistributionChart data={analytics.sector_distribution} />
      <IncidentTimelineChart data={analytics.monthly_timeline} />
      <TaskProgressChart data={analytics.task_by_specialist} />
    </div>
  );
}
